import { Box, LoadingOverlay, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { KpiCard } from "../cards/KpiCard"
import { SectionHeader } from "../cards/SectionHeader"
import classes from "../analytics.module.css"
import { SalesAnalyticsSummary } from "../types"
import { formatCompactMnt, formatDecimal, formatDelta, formatInt, formatPct } from "../utils"

type Props = {
  data: SalesAnalyticsSummary | undefined
  isLoading: boolean
}

export function SalesSection({ data, isLoading }: Props) {
  const { t } = useTranslation(["dashboard"])

  const totalSales = data?.data.totalSales
  const orderCount = data?.data.orderCount
  const avgOrder   = data?.data.avgOrder
  const discount   = data?.data.discount
  const frequency  = data?.data.frequency

  const totalSalesFmt = totalSales ? formatCompactMnt(totalSales.value) : { display: "—", unit: "" }
  const avgOrderFmt   = avgOrder   ? formatCompactMnt(avgOrder.value)   : { display: "—", unit: "" }
  const discountFmt   = discount   ? formatCompactMnt(discount.value)   : { display: "—", unit: "" }

  const totalSalesMom = formatPct(totalSales?.momPct ?? null)
  const totalSalesYoy = formatPct(totalSales?.yoyPct ?? null)
  const orderCountMom = formatPct(orderCount?.momPct ?? null)
  const orderCountYoy = formatPct(orderCount?.yoyPct ?? null)
  const avgOrderMom   = formatPct(avgOrder?.momPct ?? null)
  const discountMom   = formatPct(discount?.momPct ?? null)
  const frequencyMom  = formatDelta(frequency?.momDelta ?? null)

  return (
    <Box pos="relative" className={classes.section}>
      <LoadingOverlay visible={isLoading} />
      <SectionHeader
        title={t("analytics.sections.sales.title", { defaultValue: "Борлуулалт" })}
        description={t("analytics.sections.sales.desc", { defaultValue: "Борлуулалтын ерөнхий дүн шинжилгээ" })}
      />

      <Box className={classes.kpiGrid} data-cols={5}>
        <KpiCard
          label={t("analytics.kpis.totalSales", { defaultValue: "Нийт борлуулалт" })}
          value={totalSalesFmt.display}
          unit={totalSalesFmt.unit}
          changes={[
            { sign: totalSalesMom.sign, text: totalSalesMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" },
            { sign: totalSalesYoy.sign, text: totalSalesYoy.text, labelKey: "analytics.kpis.vsPrevYear",  label: "өмнөх жилийн мөн үе" }
          ]}
        />
        <KpiCard
          label={t("analytics.kpis.orderCount", { defaultValue: "Захиалгын тоо" })}
          value={orderCount ? formatInt(orderCount.value) : "—"}
          changes={[
            { sign: orderCountMom.sign, text: orderCountMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" },
            { sign: orderCountYoy.sign, text: orderCountYoy.text, labelKey: "analytics.kpis.vsPrevYear",  label: "өмнөх жилийн мөн үе" }
          ]}
        />
        <KpiCard
          label={t("analytics.kpis.avgOrder", { defaultValue: "Дундаж захиалга" })}
          value={avgOrderFmt.display}
          unit={avgOrderFmt.unit}
          changes={[
            { sign: avgOrderMom.sign, text: avgOrderMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }
          ]}
        />
        <KpiCard
          label={t("analytics.kpis.discount", { defaultValue: "Хөнгөлөлтийн дүн" })}
          value={discountFmt.display}
          unit={discountFmt.unit}
          changes={[
            { sign: discountMom.sign, text: discountMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }
          ]}
        />
        <KpiCard
          label={t("analytics.kpis.frequency", { defaultValue: "Давтамж" })}
          value={frequency ? formatDecimal(frequency.value, 1) : "—"}
          unit={t("analytics.kpis.frequencyUnit", { defaultValue: "удаа/харилц." })}
          changes={[
            { sign: frequencyMom.sign, text: frequencyMom.text, labelKey: "analytics.kpis.vsPrevMonth", label: "өмнөх сараас" }
          ]}
        />
      </Box>

      {!isLoading && !data ? (
        <Text className={classes.note} ta="center">
          {t("analytics.emptyState", { defaultValue: "Өгөгдөл байхгүй" })}
        </Text>
      ) : null}
    </Box>
  )
}
